import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  SelectChangeEvent,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import GoogleMaps from '../../../components/Location';
import RadioButton from '../../../components/RadioButton';
import SbTextField from '../../../components/Textfield';
import GetRatesContext from '../../../context/GetRates';
import { getFormattedLocation } from '../../../services/formateLocation';
import { GetRatesContextType, RatesInputErrorType, RatesInputType } from '../Type';
import { AddressType } from '../../../store/reducers/GetRates/Type';
import { AddressType as SavedAddressType } from '../../../store/reducers/SavedAddress/Type';
import { reverseGeocodeCurrentLocation } from '../services/geolocationService';
import { Root } from './styles';
import { RootStateType } from '@Store';
import { ValidationResult } from 'joi';
import { useSelector } from 'react-redux';
import { DropdownOptionType } from '../../../components/Dropdown/Type';
import { DefaultRatesInput, DefaultRatesInputError, WEIGHT_UNITS, defaultBox } from '../Constant';
import { SelectOptions } from '../StyleComponent';
import { documentWeight, inputRatesSchema } from './Schema';
import { useAiValidation } from '../../../hooks/useAiValidation';

const ShipmentTypeList = [
  { value: 'parcel', label: 'Parcel' },
  { value: 'document', label: 'Document' },
];

const formatAddressDisplay = (address?: AddressType) => {
  if (!address) return '';
  const parts = [(address as any).line1, (address as any).line2, address.city, address.state, address.country].filter(
    val => typeof val === 'string' && val.trim().length > 0,
  );

  if (parts.length > 0) return parts.join(', ');

  return Object.values(address)
    .filter(val => typeof val === 'string' && val.trim().length > 0)
    .join(', ');
};

export interface RatesInputProps {
  pickupSuggestions?: SavedAddressType[];
  destinationSuggestions?: SavedAddressType[];
  savedAddressesLoading?: boolean;
  savedAddressesError?: any;
}

function RatesInput({ pickupSuggestions = [], destinationSuggestions = [] }: RatesInputProps) {
  const { formData } = useSelector((state: RootStateType) => state.getRates);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const {
    hideBoxes,
    setHideBoxes,
    ratesInput,
    setRateInput,
    setBoxes,
    setRatesInputHasError,
    setValidationErrors,
    boxes,
    pickup,
    destination,
    setPickup,
    setDestination,
    setBoxCount,
  } = React.useContext(GetRatesContext) as GetRatesContextType;

  const [initials, setInitials] = useState<RatesInputType>({
    ...DefaultRatesInput,
  });
  const [pickupPinCode, setPickupPinCode] = useState<string>('');
  const [destinationPinCode, setDestinationPinCode] = useState<string>('');
  const [errors, setErrors] = useState<RatesInputErrorType>({ ...DefaultRatesInputError });
  const [loadingCurrentLocation, setLoadingCurrentLocation] = useState<{
    pickup: boolean;
    destination: boolean;
  }>({ pickup: false, destination: false });
  const [locationError, setLocationError] = useState<{
    pickup: string | null;
    destination: string | null;
  }>({ pickup: null, destination: null });

  const theme = useTheme();
  const { validationStates, validate } = useAiValidation();
  const defaultCountry = organizationData?._country?.name?.trim() || 'India';

  // Trigger AI validation for Pickup Address (shipper/source) with complete address info
  useEffect(() => {
    const countryToUse = pickup?.country?.trim() || defaultCountry;
    const pincodeToUse = pickupPinCode || pickup?.pincode || '';
    if (pincodeToUse && countryToUse) {
      validate('pickup.shipmentAddress', 'shipmentAddress', {
        city: pickup?.city || '',
        state: pickup?.state || '',
        country: countryToUse,
        pincode: pincodeToUse,
        line1: (pickup as any)?.line1 || '',
        line2: (pickup as any)?.line2 || '',
      });
    }
  }, [
    pickupPinCode,
    pickup?.pincode,
    pickup?.city,
    pickup?.state,
    pickup?.country,
    (pickup as any)?.line1,
    (pickup as any)?.line2,
    defaultCountry,
    validate,
  ]);

  // Trigger AI validation for Destination Address (consignee/destination) with complete address info
  useEffect(() => {
    const countryToUse = destination?.country?.trim() || defaultCountry;
    const pincodeToUse = destinationPinCode || destination?.pincode || '';
    if (pincodeToUse && countryToUse) {
      validate('destination.shipmentAddress', 'shipmentAddress', {
        city: destination?.city || '',
        state: destination?.state || '',
        country: countryToUse,
        pincode: pincodeToUse,
        line1: (destination as any)?.line1 || '',
        line2: (destination as any)?.line2 || '',
      });
    }
  }, [
    destinationPinCode,
    destination?.pincode,
    destination?.city,
    destination?.state,
    destination?.country,
    (destination as any)?.line1,
    (destination as any)?.line2,
    defaultCountry,
    validate,
  ]);

  useEffect(() => {
    if (formData?.boxes) {
      const { boxes, weight } = formData;
      setInitials(initials => ({ ...initials, weight }));
      setBoxCount(boxes.length);
    }
  }, [formData]);

  useEffect(() => {
    setBoxCount(boxes.length);
  }, [boxes.length]);

  useEffect(() => {
    if (pickup?.pincode !== undefined) {
      setPickupPinCode(pickup.pincode);
    }
  }, [pickup?.pincode]);

  useEffect(() => {
    if (destination?.pincode !== undefined) {
      setDestinationPinCode(destination.pincode);
    }
  }, [destination?.pincode]);

  useEffect(() => {
    const sumOfBoxWeights = boxes.reduce((total, box) => total + Number(box.weight || 0), 0.0);
    const resolvedWeight = sumOfBoxWeights > 0 ? sumOfBoxWeights : ratesInput.weight ? Number(ratesInput.weight) : 0;

    setInitials(prev => ({
      ...prev,
      pickup,
      destination,
      pickupPinCode: pickup?.pincode !== undefined ? pickup.pincode : prev.pickupPinCode,
      destinationPinCode: destination?.pincode !== undefined ? destination.pincode : prev.destinationPinCode,
      weight: resolvedWeight > 0 ? resolvedWeight : prev.weight || 0,
      weightUnit: ratesInput.weightUnit || prev.weightUnit,
      shipmentType: ratesInput.shipmentType || prev.shipmentType,
    }));
  }, [pickup, destination, boxes, ratesInput.weight, ratesInput.weightUnit, ratesInput.shipmentType]);

  useEffect(() => {
    setRateInput({ ...initials });
    const result: ValidationResult = inputRatesSchema.validate(initials, { abortEarly: false });
    const { error } = result;
    if (error?.details.length) {
      setRatesInputHasError(true);
      const errorMsgs = error.details.map(d => {
        const path = d.path.join('.');
        if (path.includes('pickupPinCode')) return 'Source pincode is required';
        if (path.includes('destinationPinCode')) return 'Destination pincode is required';
        if (path.includes('weight')) return 'Valid weight is required';
        return d.message.replace(/"/g, '');
      });
      if (setValidationErrors) {
        setValidationErrors(Array.from(new Set(errorMsgs)));
      }
    } else {
      setRatesInputHasError(false);
      if (setValidationErrors) {
        setValidationErrors([]);
      }
    }
  }, [initials]);

  const handleSelectPickupAddress = useCallback(
    (address: SavedAddressType) => {
      const updatedPickup: AddressType & Record<string, any> = {
        name: address.name || '',
        companyName: address.companyName || '',
        label: address.label || '',
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        state: address.state || '',
        country: address.country || '',
        pincode: address.pincode || '',
        email: address.email || '',
        ...(address.phone
          ? {
              phone: {
                telephoneCode: address.phone.telephoneCode || '',
                number: (address.phone.number || '').replace(/\D/g, ''),
              },
            }
          : {}),
        ...(address.alternatePhone
          ? {
              alternatePhone: {
                telephoneCode: address.alternatePhone.telephoneCode || '',
                number: (address.alternatePhone.number || '').replace(/\D/g, ''),
              },
            }
          : {}),
      };

      setPickup(updatedPickup);
      setPickupPinCode(address.pincode || '');

      setInitials(prev => ({
        ...prev,
        pickup: updatedPickup,
        pickupPinCode: address.pincode || '',
      }));

      setErrors(prev => ({ ...prev, pickupPinCode: '' }));
    },
    [setPickup, setErrors],
  );

  const handleSelectDestinationAddress = useCallback(
    (address: SavedAddressType) => {
      const updatedDestination: AddressType & Record<string, any> = {
        name: address.name || '',
        companyName: address.companyName || '',
        label: address.label || '',
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        state: address.state || '',
        country: address.country || '',
        pincode: address.pincode || '',
        email: address.email || '',
        ...(address.phone
          ? {
              phone: {
                telephoneCode: address.phone.telephoneCode || '',
                number: (address.phone.number || '').replace(/\D/g, ''),
              },
            }
          : {}),
        ...(address.alternatePhone
          ? {
              alternatePhone: {
                telephoneCode: address.alternatePhone.telephoneCode || '',
                number: (address.alternatePhone.number || '').replace(/\D/g, ''),
              },
            }
          : {}),
      };

      setDestination(updatedDestination);
      setDestinationPinCode(address.pincode || '');

      setInitials(prev => ({
        ...prev,
        destination: updatedDestination,
        destinationPinCode: address.pincode || '',
      }));

      setErrors(prev => ({ ...prev, destinationPinCode: '' }));
    },
    [setDestination, setErrors],
  );

  const handleUseCurrentLocation = useCallback(
    async (target: 'pickup' | 'destination') => {
      if (loadingCurrentLocation[target]) return;

      setLoadingCurrentLocation(prev => ({ ...prev, [target]: true }));
      setLocationError(prev => ({ ...prev, [target]: null }));

      try {
        const result = await reverseGeocodeCurrentLocation();
        const { formattedLocation } = result;

        if (target === 'pickup') {
          setPickup(formattedLocation);
          setPickupPinCode(formattedLocation.pincode || '');
          setInitials(prev => ({
            ...prev,
            pickup: formattedLocation,
            pickupPinCode: formattedLocation.pincode || '',
          }));
          setErrors(prev => ({ ...prev, pickupPinCode: '' }));
        } else {
          setDestination(formattedLocation);
          setDestinationPinCode(formattedLocation.pincode || '');
          setInitials(prev => ({
            ...prev,
            destination: formattedLocation,
            destinationPinCode: formattedLocation.pincode || '',
          }));
          setErrors(prev => ({ ...prev, destinationPinCode: '' }));
        }
      } catch (err: any) {
        console.error(`Current location error (${target}):`, err);
        setLocationError(prev => ({
          ...prev,
          [target]: err.message || 'Unable to retrieve current location.',
        }));
      } finally {
        setLoadingCurrentLocation(prev => ({ ...prev, [target]: false }));
      }
    },
    [loadingCurrentLocation, setPickup, setDestination, setErrors],
  );

  function validateProperty(event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) {
    const { name, value } = event.target;
    const obj = { [name]: value };
    const result = inputRatesSchema.validate(obj);
    const { error } = result;
    const errorData = { ...errors };
    const errorMessage = error && error.details[0].context?.key === name ? error.details[0].message : null;
    if (errorMessage && name in DefaultRatesInputError) {
      errorData[name as keyof RatesInputErrorType] = errorMessage;
    } else {
      errorData[name as keyof RatesInputErrorType] = '';
    }
    setErrors(errorData);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    validateProperty(event);
    const { name, value } = event.target;
    const data = { ...initials };
    if (name === 'pickupPinCode') {
      setPickupPinCode(value);
      setPickup({ ...pickup, pincode: value });
      data.pickupPinCode = value;
      data.pickup = { ...pickup, pincode: value };
    } else if (name === 'destinationPinCode') {
      setDestinationPinCode(value);
      setDestination({ ...destination, pincode: value });
      data.destinationPinCode = value;
      data.destination = { ...destination, pincode: value };
    } else if (name === 'shipmentType') {
      data[name] = value;
    } else if (name === 'weightUnit') {
      data[name] = value === 'kg' ? 'kg' : 'lb';
    } else if (name === 'weight') {
      data[name] = value;
      if (boxes.length <= 1) {
        setBoxes([{ ...defaultBox, weight: value }]);
      }
    }
    setInitials(data);
  }

  const handleAddressChange = useCallback(
    /* eslint-disable @typescript-eslint/no-explicit-any */
    (event: any, key: string) => {
      const address = getFormattedLocation(event.address_components);
      if (key === 'pickup') {
        setPickup({ ...address });
        setPickupPinCode(address.pincode);
      } else if (key === 'destination') {
        setDestination({ ...address });
        setDestinationPinCode(address.pincode);
      }
    },
    [initials],
  );

  const handleShipmentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
    setInitials(initials => ({
      ...initials,
      weight: DefaultRatesInput.weight,
      weightUnit: DefaultRatesInput.weightUnit,
    }));
  };

  return (
    <Root container item className="w-full" sx={{ rowGap: 3 }}>
      {name !== 'Cargo Solutions Network' ? (
        <>
          {/* 1. Location Details: Source & Destination Cards */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Source (Pickup) Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#ffffff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '10px',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                        }}
                      >
                        1
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                          Source (Pickup)
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem' }}
                        >
                          Pickup location and origin details
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {locationError.pickup && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '6px',
                        display: 'block',
                      }}
                    >
                      ⚠️ {locationError.pickup}
                    </Typography>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                      <InputLabel className="mb-1" required={name !== 'Cargo Solutions Network'}>
                        Pickup Location
                      </InputLabel>
                      <GoogleMaps
                        onSelect={event => handleAddressChange(event, 'pickup')}
                        placeholder="Search Pickup Location / City"
                        required={name === 'Cargo Solutions Network' ? false : true}
                        value={formatAddressDisplay(pickup)}
                        onUseCurrentLocation={() => handleUseCurrentLocation('pickup')}
                        loadingCurrentLocation={loadingCurrentLocation.pickup}
                        savedAddressesTitle="Saved Pickup Addresses"
                        savedAddresses={pickupSuggestions}
                        onSelectSavedAddress={handleSelectPickupAddress}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5} className="mb-input">
                      <SbTextField
                        label="Pickup Pincode"
                        placeholder="e.g. 395003"
                        required
                        value={pickupPinCode}
                        onChange={handleChange}
                        name="pickupPinCode"
                        error={errors.pickupPinCode}
                        validationState={validationStates['pickup.shipmentAddress']}
                      />
                    </Grid>
                  </Grid>

                  {formatAddressDisplay(pickup) ? (
                    <Box
                      sx={{
                        mt: 'auto',
                        px: 1.5,
                        py: 1,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        border: '1px dashed',
                        borderColor: alpha(theme.palette.primary.main, 0.25),
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}
                      >
                        Selected Address:{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}
                        >
                          {formatAddressDisplay(pickup)}
                        </Typography>
                      </Typography>
                    </Box>
                  ) : null}
                </Paper>
              </Grid>

              {/* Destination Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#ffffff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '10px',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                        }}
                      >
                        2
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                          Destination (Delivery)
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem' }}
                        >
                          Delivery destination and recipient area
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {locationError.destination && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '6px',
                        display: 'block',
                      }}
                    >
                      ⚠️ {locationError.destination}
                    </Typography>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                      <InputLabel className="mb-1" required>
                        Destination Location
                      </InputLabel>
                      <GoogleMaps
                        onSelect={event => handleAddressChange(event, 'destination')}
                        placeholder="Search Destination Location / City"
                        required
                        value={formatAddressDisplay(destination)}
                        onUseCurrentLocation={() => handleUseCurrentLocation('destination')}
                        loadingCurrentLocation={loadingCurrentLocation.destination}
                        savedAddressesTitle="Saved Destination Addresses"
                        savedAddresses={destinationSuggestions}
                        onSelectSavedAddress={handleSelectDestinationAddress}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5} className="mb-input">
                      <SbTextField
                        placeholder="e.g. 560055"
                        label="Destination Pincode"
                        required
                        value={destinationPinCode}
                        onChange={handleChange}
                        name="destinationPinCode"
                        error={errors.destinationPinCode}
                        validationState={validationStates['destination.shipmentAddress']}
                      />
                    </Grid>
                  </Grid>

                  {formatAddressDisplay(destination) ? (
                    <Box
                      sx={{
                        mt: 'auto',
                        px: 1.5,
                        py: 1,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        border: '1px dashed',
                        borderColor: alpha(theme.palette.primary.main, 0.25),
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}
                      >
                        Selected Address:{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}
                        >
                          {formatAddressDisplay(destination)}
                        </Typography>
                      </Typography>
                    </Box>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* 2. Shipment & Package Details Card */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#ffffff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                  }}
                >
                  3
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    Shipment & Package Details
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem' }}>
                    Specify package type, weight, and volumetric details
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <RadioButton
                    label="Shipment Type"
                    required
                    containerClass="w-full"
                    name="shipmentType"
                    onChange={handleShipmentTypeChange}
                    defaultValue={initials.shipmentType}
                    value={initials.shipmentType}
                    list={ShipmentTypeList}
                    sx={{ display: 'flex', columnGap: 3 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <SbTextField
                    type="number"
                    placeholder="Weight"
                    label="Weight"
                    required
                    value={initials.weight ? initials.weight.toString() : ''}
                    onChange={handleChange}
                    name="weight"
                    error={errors.weight}
                    endAdornment={
                      <FormControl style={{ width: '50%' }}>
                        <SelectOptions
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={initials.weightUnit}
                          name="weightUnit"
                          renderValue={value => <>{value}</>}
                          onChange={handleChange}
                        >
                          {WEIGHT_UNITS?.map(({ display, value }: DropdownOptionType) => (
                            <MenuItem key={value} value={value}>
                              {display}
                            </MenuItem>
                          ))}
                        </SelectOptions>
                      </FormControl>
                    }
                  />
                  {initials.shipmentType === 'document' && (
                    <FormHelperText className="m-0" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                      {`For shipment type document, weight should be less than ${documentWeight[initials.weightUnit]} ${
                        initials.weightUnit
                      }`}
                    </FormHelperText>
                  )}
                </Grid>

                {initials.shipmentType === 'parcel' && (
                  <Grid item xs={12} sm={12} md={4}>
                    <Box
                      onClick={() => setHideBoxes(!hideBoxes)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.5,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: !hideBoxes ? theme.palette.primary.main : 'divider',
                        bgcolor: !hideBoxes ? alpha(theme.palette.primary.main, 0.04) : '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      <Checkbox
                        name="insurance"
                        checked={!hideBoxes}
                        onChange={() => setHideBoxes(!hideBoxes)}
                        inputProps={{ 'aria-label': 'controlled' }}
                        sx={{ p: 0.5 }}
                      />
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Add Box-wise Details</Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem' }}
                        >
                          Calculate volumetric & dimensional weight
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#ffffff',
            maxWidth: '480px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <Box>
            <Box
              sx={{
                mb: 1,
              }}
            >
              <InputLabel required>Destination Location</InputLabel>
            </Box>

            {locationError.destination && (
              <Typography
                variant="caption"
                sx={{
                  color: 'error.main',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '6px',
                  display: 'block',
                  mb: 1,
                }}
              >
                ⚠️ {locationError.destination}
              </Typography>
            )}

            <GoogleMaps
              onSelect={event => handleAddressChange(event, 'destination')}
              placeholder="Destination Location"
              required
              value={formatAddressDisplay(destination)}
              onUseCurrentLocation={() => handleUseCurrentLocation('destination')}
              loadingCurrentLocation={loadingCurrentLocation.destination}
              savedAddressesTitle="Saved Destination Addresses"
              savedAddresses={destinationSuggestions}
              onSelectSavedAddress={handleSelectDestinationAddress}
            />
            {formatAddressDisplay(destination) ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                {formatAddressDisplay(destination)}
              </Typography>
            ) : null}
          </Box>
          <Box>
            <SbTextField
              placeholder="Destination Pincode"
              label="Destination Pincode"
              value={destinationPinCode}
              onChange={handleChange}
              name="destinationPinCode"
              error={errors.destinationPinCode}
              validationState={validationStates['destination.shipmentAddress']}
            />
          </Box>
          <Box>
            <SbTextField
              type="number"
              placeholder="Weight"
              label="Weight"
              required
              value={initials.weight ? initials.weight.toString() : ''}
              onChange={handleChange}
              name="weight"
              error={errors.weight}
              endAdornment={
                <FormControl style={{ width: '25%' }}>
                  <SelectOptions
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={initials.weightUnit}
                    name="weightUnit"
                    renderValue={value => <>{value}</>}
                    onChange={handleChange}
                  >
                    {WEIGHT_UNITS?.map(({ display, value }: DropdownOptionType) => (
                      <MenuItem key={value} value={value}>
                        {display}
                      </MenuItem>
                    ))}
                  </SelectOptions>
                </FormControl>
              }
            />
            {initials.shipmentType === 'document' && (
              <FormHelperText className="m-0" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                {`For shipment type document, weight should be less than ${documentWeight[initials.weightUnit]} ${
                  initials.weightUnit
                }`}
              </FormHelperText>
            )}
          </Box>
        </Paper>
      )}
    </Root>
  );
}

export default RatesInput;
